import { ReactElement, useState } from 'react';
import { InputNumber, ModalProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { ENV_ASSET_CODE, ENV_ASSET_ISSUER, ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { notifyErrorMessage, notifySuccess } from 'common/utils/notifier.utils';
import { useBurnEmissionMutation, useIssueEmissionMutation } from 'domains/emission/queries/emission.query';
import { TIssueBurnEmissionReqParams } from 'domains/emission/types/emission.types';
import { ETokenModalFormFields, ETokenModalType } from './token-modal.enum';
import { SContainer, SFooterInner } from './token-modal.style';
import { Body, Header, Footer, EnhancedModal } from '../EnhancedModal/EnhancedModal';

export type TokenModalOperationsProps = Omit<ModalProps, 'onCancel' | 'onOk'> & {
  type: ETokenModalType;
  onCancel: () => void;
  maxAmount?: number;
};

const TokenModalComponent = ({ type, onCancel, maxAmount, ...restProps }: TokenModalOperationsProps): ReactElement => {
  const { t } = useTranslation();
  const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);

  const { mutateAsync: mutateAsyncIssueAssetMutation } = useIssueEmissionMutation();

  const { mutateAsync: mutateAsyncBurnAssetMutation } = useBurnEmissionMutation();

  const onFormSubmitHandler = async (values: Record<ETokenModalFormFields, string>): Promise<void> => {
    try {
      setIsSubmitBtnLoading(true);

      const { amount, reserveBalance } = values;

      const reqParams = {
        amount: Number(amount),
        asset_code: ENV_ASSET_CODE,
        asset_issuer: ENV_ASSET_ISSUER,
      } as TIssueBurnEmissionReqParams;

      if (reserveBalance) reqParams.reserve_balance = Number(reserveBalance);

      if (type === ETokenModalType.Issue) {
        try {
          await mutateAsyncIssueAssetMutation({
            counterpartyGUID: ENV_COUNTERPARTY_GUID,
            params: reqParams,
          });
          notifySuccess('dashboard.tokenModal.notification.success.issue.request');
        } catch (error) {
          notifyErrorMessage(error, 'dashboard.tokenModal.notification.error.issue.response');
        }
      } else {
        try {
          await mutateAsyncBurnAssetMutation({
            counterpartyGUID: ENV_COUNTERPARTY_GUID,
            params: reqParams,
          });
          notifySuccess('dashboard.tokenModal.notification.success.burn.request');
        } catch (error) {
          notifyErrorMessage(error, 'dashboard.tokenModal.notification.error.burn.response');
        }
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.AccountQueryKeys].getCounterpartyAccountDetails._def,
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.EmissionQueryKeys].getEmissionHistory_MultiRole._def,
      });

      setIsSubmitBtnLoading(false);
      onCancel();
    } catch (error) {
      console.error(error);
      setIsSubmitBtnLoading(false);
    }
  };

  const positiveNumbersValidationRegExp = '^[+]?\\d+(\\.\\d+)?$';

  return (
    <EnhancedModal
      width={364}
      onCancel={(): void => {
        onCancel();
      }}
      {...restProps}
    >
      <Form
        initialValues={{
          amount: 1,
        }}
        onFinish={onFormSubmitHandler}
      >
        <SContainer>
          <Header>{type === ETokenModalType.Issue
            ? t('dashboard.tokenModal.title.issueECurrency')
            : t('dashboard.tokenModal.title.burnECurrency')}
          </Header>
          <Body>
            <Form.Item
              name={ETokenModalFormFields.ReserveBalance}
              label={t('dashboard.tokenModal.input.issueReserveBalance')}
              rules={[
                {
                  pattern: new RegExp(positiveNumbersValidationRegExp),
                  message: t('dashboard.tokenModal.form.validation') as string,
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name={ETokenModalFormFields.Amount}
              label={type === ETokenModalType.Issue
                ? t('dashboard.tokenModal.input.issueAmount')
                : t('dashboard.tokenModal.input.burnAmount')}
              rules={[
                {
                  required: true,
                  message: t('dashboard.tokenModal.form.validation') as string,
                },
                {
                  pattern: new RegExp(positiveNumbersValidationRegExp),
                  message: t('dashboard.tokenModal.form.validation') as string,
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} max={maxAmount} />
            </Form.Item>
          </Body>
          <Footer>
            <SFooterInner>
              <Button
                block
                onClick={(): void => {
                  onCancel();
                }}
              >
                {t('dashboard.tokenModal.button.cancel')}
              </Button>
              <div style={{ width: 16, flexShrink: 0 }} />
              <Button loading={isSubmitBtnLoading} block type="primary" htmlType="submit">
                {type === ETokenModalType.Issue ? t('common.issue') : t('common.burn')}
              </Button>
            </SFooterInner>
          </Footer>
        </SContainer>
      </Form>
    </EnhancedModal>
  );
};

export default TokenModalComponent;
